import { createColumnHelper } from "@tanstack/react-table";
import { useMemo } from "react";
import TeamCellWithLocation, {
  TeamCellWithLocationProps,
} from "@/components/headToHead/Table/teamCellWithLocation";
import { formatDate, MONTH_DATE } from "@/utils/date";
import { createTableData } from "./utils";
import type {
  GetHeadToHeadTeamStatsTabQuery,
  TeamGameLog,
} from "@/graphql/gql/graphql";
import type { CellContext } from "@tanstack/react-table";

type Datum = {
  team: TeamCellWithLocationProps;
} & Pick<
  TeamGameLog,
  | "timestamp"
  | "score"
  | "week"
  | "passingYards"
  | "passingTDs"
  | "opponentPassingRank"
  | "rushingYards"
  | "rushingTDs"
  | "opponentRushingRank"
  | "passYardsAllowed"
  | "opponentPassDefRank"
  | "rushYardsAllowed"
  | "opponentRushDefRank"
>;

const renderTeamCell = (row: CellContext<Datum, TeamCellWithLocationProps>) => (
  <TeamCellWithLocation
    homeTeam={row.getValue().homeTeam}
    opponentTeam={row.getValue().opponentTeam}
  />
);

const renderDateCell = (row: CellContext<Datum, number>) => {
  const unixSecondsToMilliseconds = row.getValue() * 1000;
  const date = new Date(unixSecondsToMilliseconds);
  return formatDate(date, MONTH_DATE);
};

const createColumns = () => {
  const columnHelper = createColumnHelper<Datum>();

  return [
    columnHelper.group({
      id: "team",
      header: "",
      columns: [
        columnHelper.accessor("team", {
          header: "Team",
          cell: renderTeamCell,
        }),
      ],
    }),
    columnHelper.group({
      id: "gameInfo",
      header: "",
      columns: [
        columnHelper.group({
          id: "gameInfo",
          header: "",
          columns: [
            columnHelper.accessor("timestamp", {
              header: "Date",
              cell: renderDateCell,
            }),
            columnHelper.accessor("score", {
              header: "Result",
            }),
            columnHelper.accessor("week", {
              header: "Week",
            }),
          ],
        }),
      ],
    }),
    columnHelper.group({
      id: "offense",
      header: "Offense",
      columns: [
        columnHelper.group({
          header: "Pass",
          columns: [
            columnHelper.accessor("passingYards", {
              header: "Yards",
            }),
            columnHelper.accessor("passingTDs", {
              header: "TDS",
            }),
            columnHelper.accessor("opponentPassingRank", {
              header: "OPP Rank",
            }),
          ],
        }),
        columnHelper.group({
          header: "Rush",
          columns: [
            columnHelper.accessor("rushingYards", {
              header: "Yards",
            }),
            columnHelper.accessor("rushingTDs", {
              header: "TDS",
            }),
            columnHelper.accessor("opponentRushingRank", {
              header: "OPP Rank",
            }),
          ],
        }),
      ],
    }),
    columnHelper.group({
      id: "defense",
      header: "Defense",
      columns: [
        columnHelper.group({
          header: "Pass Defense",
          columns: [
            columnHelper.accessor("passYardsAllowed", {
              header: "Yards",
            }),
            columnHelper.accessor("opponentPassDefRank", {
              header: "Opp Rank",
            }),
          ],
        }),
        columnHelper.group({
          header: "Rush Defense",
          columns: [
            columnHelper.accessor("rushYardsAllowed", {
              header: "Yards",
            }),
            columnHelper.accessor("opponentRushDefRank", {
              header: "OPP Rank",
            }),
          ],
        }),
      ],
    }),
  ];
};

export enum TeamTab {
  Home = "Home",
  Away = "Away",
}

const useGameLogTable = (data: GetHeadToHeadTeamStatsTabQuery["event"]) => {
  const tableData = useMemo(() => createTableData(data), [data]);
  const columns = useMemo(createColumns, []);

  return {
    columns,
    tableData,
  };
};

export default useGameLogTable;
